import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Droplets, Loader2, CheckCircle2, PhoneCall } from "lucide-react";

export default function PublicForm() {
  const { slug } = useParams();
  const [meta, setMeta] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", problem_description: "", zip_code: "", preferred_time: "" });

  useEffect(() => {
    api.get(`/public/form/${slug}`).then(({ data }) => setMeta(data)).catch(() => setNotFound(true));
  }, [slug]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post(`/public/leads/${slug}`, form);
      setSubmitted(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (notFound) return <div className="min-h-screen flex items-center justify-center text-slate-500">This booking form was not found.</div>;
  if (!meta) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="min-h-screen bg-slate-50 grid-dots py-10 px-4">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-6">
          <div className="h-12 w-12 rounded-xl bg-blue-600 flex items-center justify-center mx-auto mb-3"><Droplets className="h-6 w-6 text-white" /></div>
          <h1 className="text-2xl font-bold font-heading" data-testid="public-form-business-name">{meta.business_name}</h1>
          <p className="text-slate-500 mt-1">{meta.description}</p>
          {meta.phone && <p className="mt-2 text-sm text-slate-600 flex items-center justify-center gap-1.5"><PhoneCall className="h-4 w-4" /> {meta.phone}</p>}
        </div>
        <Card className="p-8 shadow-sm">
          {submitted ? (
            <div className="text-center py-6" data-testid="public-form-success">
              <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto" />
              <h2 className="mt-4 text-xl font-bold font-heading">Request received!</h2>
              <p className="mt-2 text-slate-600">Thanks — {meta.business_name} got your request and will reach out shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <h2 className="text-lg font-semibold font-heading">{meta.title}</h2>
              <div><Label>Full name *</Label><Input value={form.name} onChange={set("name")} required data-testid="public-name" className="mt-1" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Phone *</Label><Input value={form.phone} onChange={set("phone")} required data-testid="public-phone" className="mt-1" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="public-email" className="mt-1" /></div>
              </div>
              <div>
                <Label>Service needed</Label>
                {meta.services?.length ? (
                  <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                    <SelectTrigger className="mt-1" data-testid="public-service"><SelectValue placeholder="Select a service" /></SelectTrigger>
                    <SelectContent>
                      {meta.services.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={form.service} onChange={set("service")} data-testid="public-service-input" className="mt-1" placeholder="e.g. Water heater repair" />
                )}
              </div>
              <div><Label>Describe the problem</Label><Textarea value={form.problem_description} onChange={set("problem_description")} data-testid="public-problem" className="mt-1" rows={3} placeholder="Tell us what's going on..." /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>ZIP code</Label><Input value={form.zip_code} onChange={set("zip_code")} data-testid="public-zip" className="mt-1" /></div>
                <div><Label>Preferred time</Label><Input value={form.preferred_time} onChange={set("preferred_time")} data-testid="public-time" className="mt-1" placeholder="e.g. Tomorrow morning" /></div>
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <Button type="submit" disabled={loading} data-testid="public-submit-btn" className="w-full h-11 bg-blue-600 hover:bg-blue-700">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request service"}
              </Button>
              <p className="text-center text-xs text-slate-400">No account needed. We'll contact you about your request.</p>
            </form>
          )}
        </Card>
        <p className="text-center text-xs text-slate-400 mt-6">Powered by PlumbFlow</p>
      </div>
    </div>
  );
}
